import { defineStore } from "pinia";
import axios from "axios";
import { BACKEND_URL } from "../variables.js";

// POST a JSON body as a string under a `text/plain` content type instead of
// letting axios tag it `application/json`.
//
// The backend answers a preflight with `Access-Control-Allow-Origin: *` but no
// `Access-Control-Allow-Headers`, so an `application/json` body — a
// non-safelisted content type, and therefore preflighted — is blocked by the
// browser from any origin other than the deployed one (the dev server), and
// surfaces as an opaque axios "Network Error". `text/plain` is CORS-safelisted,
// so the request skips the preflight; the endpoints read the body off the raw
// request stream either way. Same workaround MarketOverviewStore documents.
const postJson = (url, body) =>
  axios.post(url, JSON.stringify(body), { headers: { "Content-Type": "text/plain" } })

export const useProjectStore = defineStore("ProjectStore", {
    state: () => ({project: {}, mlsFP: [], mlsParking: [], mlsFloorLevel: []}),
    actions: {
      setProject(projectData) {
          this.project = projectData
      },
      setOptions({mlsFp, mlsParking, mlsFloorLevel}) {
          this.mlsFP = mlsFp
          this.mlsParking = mlsParking
          this.mlsFloorLevel = mlsFloorLevel
      },

      // One Permit Application node (bundle `application`) plus the City Based
      // Application Stage options, in a single call. `ref` is either a node id
      // or a permit number (e.g. "DP-2026-00056") — the endpoint resolves both.
      async fetchPermitApplication(ref) {
        const key = /^\d+$/.test(String(ref).trim()) ? "nid" : "permit"
        const { data } = await axios.get(`${BACKEND_URL}api/permit_application/get`, {
          params: { [key]: String(ref).trim() },
        })
        if (!data || data.status !== "ok") {
          throw new Error((data && data.message) || `No permit application found for ${ref}.`)
        }
        return data
      },

      // Permit applications whose title OR folder / permit number contains `q`,
      // newest first. Titles repeat across a project's milestone nodes, so this
      // answers with candidates — the caller picks one and loads it by nid.
      async searchPermitApplications(q, limit) {
        const { data } = await axios.get(`${BACKEND_URL}api/permit_application/search`, {
          params: { q: String(q).trim(), limit },
        })
        if (!data || data.status !== "ok") {
          throw new Error((data && data.message) || "Could not search permit applications.")
        }
        return data
      },

      // Write an existing Permit Application node. Only the keys present in
      // `fields` are written; every other field on the node is left untouched,
      // and "" clears one. Admin or editor only.
      //
      // Goes through api/permit_application/save, which addresses a node by
      // `nid` exactly as it creates one from `pid`. It used to post to
      // api/presale/edit; that endpoint has been removed from the backend
      // module, and both paths always shared one writer server-side
      // (_presale_application_write), so the behaviour is unchanged.
      async editPermitApplication(nid, fields) {
        return this.savePermitApplication({ nid, ...fields })
      },

      // One level of the City Based Application Stage vocabulary: `parent` 0 (or
      // omitted) gives the top level, a tid gives that term's children. The
      // field is a City -> Permit Type -> Stage cascade storing the LEAF tid,
      // and it carries a branch per city, so it is walked a level at a time
      // rather than shipped whole. Each entry carries `has_children`, which is
      // what tells a caller whether it is a valid value yet.
      //
      // Unused for now: PermitApplicationForm picks against the static tree in
      // data/cityApplicationStages.js. Kept so switching back is a one-line
      // change in the form.
      async fetchPermitApplicationStageLevel(parent) {
        const params = { options: 1 }
        if (parent) params.parent = parent
        const { data } = await axios.get(`${BACKEND_URL}api/permit_application/get`, { params })
        if (!data || data.status !== "ok") {
          throw new Error((data && data.message) || "Could not load the application stages.")
        }
        if (Array.isArray(data.stage_level)) return data.stage_level
        // A backend that predates `stage_level` answers with the whole flat
        // tree instead (tid / name / depth / parent). Derive the level asked
        // for rather than showing an empty picker against it.
        if (Array.isArray(data.stage_options)) {
          const tree = data.stage_options
          const wanted = Number(parent) || 0
          const hasKids = new Set(tree.map((t) => Number(t.parent) || 0))
          return tree
            .filter((t) => (Number(t.parent) || 0) === wanted)
            .map((t) => ({ tid: t.tid, name: t.name, has_children: hasKids.has(Number(t.tid)) }))
        }
        return []
      },

      // POST api/presale_project/create (presale_project_create). Creates the
      // presale project node together with its first project type collection
      // (the building) and unit type collection (the component) — both are
      // always written, because api/get_single_project reads them without
      // guarding for their absence and a bare node would not open.
      //
      // Answers { nid, fid, uid, warnings }: the project, its building and its
      // component, named as the rest of the API names them.
      //
      // A refused duplicate — an LTSA PID already on a project, or the same
      // title at the same address — comes back as status "error" carrying
      // `exists` and the existing `nid`, and is RETURNED rather than thrown:
      // the caller offers to open that project or to re-send with
      // `allow_duplicate`. Anything else throws. Admin or editor only.
      async createPresaleProject(payload) {
        const { data } = await postJson(`${BACKEND_URL}api/presale_project/create`, payload)
        if (!data || data.status !== "ok") {
          if (data && data.exists) return data
          throw new Error((data && data.message) || "Could not create the project.")
        }
        return data
      },

      // POST api/presale_project/add_building (presale_project_add_building).
      // Adds a project type collection — a building — to the project `pid`,
      // optionally with its first component under `utc`. Answers
      // { pid, fid, uid, warnings }; `uid` is null when no component was given.
      //
      // A building with no component is a state the project page renders, so
      // one whose unit types are not settled yet is entered as itself and its
      // components filed afterwards. Admin or editor only.
      async addProjectBuilding(payload) {
        const { data } = await postJson(`${BACKEND_URL}api/presale_project/add_building`, payload)
        if (!data || data.status !== "ok") {
          throw new Error((data && data.message) || "Could not add the building.")
        }
        return data
      },

      // POST api/presale_project/add_component (presale_project_add_component).
      // Adds a unit type collection — a component — to the building `fid` of
      // project `pid`. Both ids are required and the server refuses an `fid`
      // that belongs to another project, so a component cannot be filed against
      // someone else's tower. Answers { pid, fid, uid, warnings }.
      // Admin or editor only.
      async addProjectComponent(payload) {
        const { data } = await postJson(`${BACKEND_URL}api/presale_project/add_component`, payload)
        if (!data || data.status !== "ok") {
          throw new Error((data && data.message) || "Could not add the component.")
        }
        return data
      },

      // The taxonomy options a project is classified by, in one call: general
      // unit type (tenure), project type and project status — the three term
      // fields the create forms offer. Each endpoint answers [{ id, label }];
      // a failing one yields an empty list rather than taking the others down
      // with it, so a form can still be filled in against the fields that did
      // load.
      async fetchProjectTermOptions() {
        const sources = {
          generalUnitTypes: "api/get_gut",
          projectTypes: "api/get_types",
          statuses: "api/get_project_status",
        }
        const keys = Object.keys(sources)
        const results = await Promise.all(
          keys.map((key) =>
            axios
              .get(`${BACKEND_URL}${sources[key]}`)
              .then(({ data }) => (Array.isArray(data) ? data : []))
              .catch((err) => {
                console.error(`ProjectStore fetchProjectTermOptions ${sources[key]} error:`, err)
                return []
              })
          )
        )
        return keys.reduce((options, key, i) => {
          options[key] = results[i]
          return options
        }, {})
      },

      // POST api/permit_application/save (presale_application_save). Adds a
      // Permit Application node under the presale project `pid` and references
      // it from field_application_ref; pass `nid` instead to write an existing
      // one. Idempotent on field_folder_permit_ — re-sending a permit number
      // the project already carries updates that node rather than duplicating
      // the milestone. Admin or editor only.
      async savePermitApplication(payload) {
        const { data } = await postJson(`${BACKEND_URL}api/permit_application/save`, payload)
        if (!data || data.status !== "ok") {
          throw new Error((data && data.message) || "Could not save the permit application.")
        }
        return data
      },

      // POST api/permit_application/upload (presale_application_upload). Turns
      // browser File objects into managed files and answers with their fids —
      // nothing is attached to a node here. The caller puts the fids in
      // field_planning_documents_type_co on the next savePermitApplication(),
      // which writes the whole set of attachments. `files` is a FileList or an
      // array of File. Admin or editor only.
      async uploadPermitApplicationFiles(files) {
        const body = new FormData()
        Array.from(files || []).forEach((file) => body.append("files[]", file, file.name))
        // No Content-Type header: the browser has to set the multipart boundary.
        const { data } = await axios.post(`${BACKEND_URL}api/permit_application/upload`, body)
        if (!data || data.status !== "ok") {
          throw new Error((data && data.message) || "Could not upload the planning documents.")
        }
        return data
      },

      // POST api/permit_application/delete (presale_application_delete). Removes
      // the milestone `nid` from the presale project `pid` — it drops the
      // field_application_ref delta on the project node or its type collection,
      // then deletes the application node once nothing references it any more.
      // Pass `detach_only` to keep the node. Admin or editor only.
      async deletePermitApplication(payload) {
        const { data } = await postJson(`${BACKEND_URL}api/permit_application/delete`, payload)
        if (!data || data.status !== "ok") {
          throw new Error((data && data.message) || "Could not remove the permit application.")
        }
        return data
      },

      // Deleting a Permit Application is a super admin action. Every other admin
      // files a delete REQUEST, which only takes effect once a super admin
      // approves it. The backend enforces this (api/permit_application/delete
      // answers `super_admin_required`); `canDelete` only picks which call the
      // UI makes. Resolves to { requested: false } after a real delete and
      // { requested: true, request } after a request.
      async removeOrRequestPermitApplication(payload, canDelete) {
        if (canDelete) {
          await this.deletePermitApplication(payload)
          return { requested: false }
        }
        const { data } = await postJson(`${BACKEND_URL}api/permit_application/request_delete`, payload)
        if (!data || data.status !== "ok") {
          throw new Error((data && data.message) || "Could not request the deletion.")
        }
        return { requested: true, request: data.request }
      },

      // GET api/permit_application/delete_requests. Pending requests for the
      // project `pid` (any admin may read them, so a row can show "delete
      // pending"). Pass `status` ("approved", "rejected", "all"…) for history.
      async fetchPermitDeleteRequests(pid, status = "pending") {
        const { data } = await axios.get(`${BACKEND_URL}api/permit_application/delete_requests`, {
          params: { pid, status },
        })
        if (!data || data.status !== "ok") {
          throw new Error((data && data.message) || "Could not load the delete requests.")
        }
        return Array.isArray(data.requests) ? data.requests : []
      },

      // Super admin only: approving runs the delete; rejecting leaves the
      // application where it is. The requester (or a super admin) can cancel.
      async approvePermitDelete(id) {
        return this.resolvePermitDelete("approve_delete", { id }, "Could not approve the deletion.")
      },
      async rejectPermitDelete(id, note = "") {
        return this.resolvePermitDelete("reject_delete", { id, note }, "Could not reject the deletion.")
      },
      async cancelPermitDelete(id) {
        return this.resolvePermitDelete("cancel_delete", { id }, "Could not cancel the request.")
      },
      async resolvePermitDelete(action, payload, fallbackMessage) {
        const { data } = await postJson(`${BACKEND_URL}api/permit_application/${action}`, payload)
        if (!data || data.status !== "ok") {
          throw new Error((data && data.message) || fallbackMessage)
        }
        return data
      },

      // POST api/utc_files/upload (presaledb_utc_files_upload). Appends files
      // to one of the UTC's multiupload fields — `field` is
      // "field_site_plan_floor_plates" or "field_features" — and answers with
      // the updated `fields` (same shape as fetchUtcFiles). `files` is a FileList or an array of File.
      // A partial batch comes back "ok" with the dropped files in `errors`.
      // Admin or editor only.
      async uploadUtcFiles(uid, field, files) {
        const body = new FormData()
        body.append("uid", uid)
        body.append("field", field)
        Array.from(files || []).forEach((file) => body.append("files[]", file, file.name))
        // No Content-Type header: the browser has to set the multipart boundary.
        const { data } = await axios.post(`${BACKEND_URL}api/utc_files/upload`, body)
        if (!data || data.status !== "ok") {
          throw new Error((data && data.message) || "Could not upload the files.")
        }
        return data
      },

      // GET api/utc_files (presaledb_utc_files_get). The files currently on the
      // UTC's Site Plan & Floor Plates and Features fields, as
      // { fields: { field_site_plan_floor_plates: [...], field_features: [...] } }.
      async fetchUtcFiles(uid) {
        const { data } = await axios.get(`${BACKEND_URL}api/utc_files`, { params: { uid } })
        if (!data || data.status !== "ok") {
          throw new Error((data && data.message) || "Could not load the files.")
        }
        return data
      },

      // POST api/utc_basic_fields/save with field_attached_files_add: records which
      // kinds of file were just uploaded on the UTC. Merge-only — the backend adds
      // to what the UTC already lists, so a stale client list cannot drop a type.
      async addAttachedFileTypes(uid, types) {
        const list = Array.from(types || [])
        if (!list.length) return null
        const { data } = await postJson(`${BACKEND_URL}api/utc_basic_fields/save`, { uid, field_attached_files_add: list })
        if (!data || data.status !== "ok") {
          throw new Error((data && data.message) || "Could not record the file types.")
        }
        return data
      },

      // POST api/utc_files/delete (presaledb_utc_files_delete). Removes file
      // `fid` from `field` on the UTC and answers with the updated `fields`.
      async deleteUtcFile(uid, field, fid) {
        const { data } = await postJson(`${BACKEND_URL}api/utc_files/delete`, { uid, field, fid })
        if (!data || data.status !== "ok") {
          throw new Error((data && data.message) || "Could not delete the file.")
        }
        return data
      },
    },
})
